import React, { useState, useReducer, useMemo, useCallback } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import produce from 'immer'

import { FormRow, FormCol } from 'style/styled'
import { useModalBillPdf } from 'hooks/useModalBillPdf'
import { listCashRegisters } from 'gql/query'
import { ContentBlock, ExpandableBlocks, ContentBlockLoading } from 'components/ContentBlock'
import { Text, Number, Select, MoreLessEquals } from 'components/forms'
import Button from 'components/Button'
import Divider from 'components/Divider'
import DxDataTable from 'components/DxDataTable'
import FilterCardRemote from 'components/FilterCard/FilterCardRemote'
import { useQuery } from 'components/graphql'
import ContentHeader from 'components/ContenHeader'
import { listProducts } from 'sections/product/gql/query'
import { listUsers } from 'sections/user/gql'
import listCreditClients from 'sections/creditClient/gql/listCreditClients'
import useLocale from 'hooks/useLocale'

import { initSearchBills, getStatisticsData } from './searchBills'
import { getMaxTableHeight, useStatisticsData } from '../helpers'
import { getBillCols, getBillItemCols } from './dxColumns'
import { CashRegisters, DatePeriod } from '../filterFragments'
import { CardsRow, CardCol } from '../filterFragments/styled'

function variablesReducre(state, { value, action }) {
	return produce(state, draft => {
		switch(action) {
			case 'dateTime':
				draft.from = value.from
				draft.to = value.to
				break
			default:
				draft[action] = value
		}
	})
}

const BillSearch = () => {
	const [expandedId, setExpandedId] = useState('0')
	const [selectedBill, setSelectedBill] = useState(null)
	const { data: crData } = useQuery(listCashRegisters, { fetchPolicy: 'cache-first' })
	const [variables, dispatchVariables] = useReducer(variablesReducre, {
		from: moment().utc().startOf('day').toISOString(),
		to: moment().utc().endOf('day').toISOString(),
		cashRegisters: crData?.listCashRegisters?.map(({ id }) => id) || [],
		products: []
	})

	const {
		handlePdfClick, pdfsLoading,
		closeModal, ModalComponent
	} = useModalBillPdf()

	const [t] = useTranslation()
	const locales = useLocale()

	const billCols = useMemo(() => getBillCols({ t, locales, handlePdfClick, pdfsLoading }), [getBillCols, pdfsLoading])
	const billItemCols = useMemo(() => getBillItemCols({ t, locales }), [getBillItemCols, locales])
	const getTableHeight = useCallback(() => getMaxTableHeight('billsTable'))
	const areaTranslation = t('common.strings.area')
	const discountNumberTransl = t('statistic_section.strings.number_discount')

	const { getStatistics, status, data, loading, called } = useStatisticsData({
		getQuery: getStatisticsData,
		initQuery: initSearchBills
	})

	const { from, to } = variables
	const isDatePeriod = moment(to).diff(moment(from), 'days') >= 1

	const handleSearchBills = e => {
		getStatistics({
			variables: { params: {
				...variables,
				from,
				to: isDatePeriod ? to : moment(from).add(1, 'day').toISOString()
			} }
		})
		setExpandedId('1')
	}

	//open PDF file
	if(closeModal) {
		return ModalComponent
	}

	return(
		<ExpandableBlocks expandedId={expandedId} onExpand={setExpandedId}>
			<ContentBlock blockId='0' startExpanded={true} label={t('common.strings.settings')}>
				<ExpandableBlocks nested>
					<FormRow>
						<FormCol narrow={!isDatePeriod}>
							<DatePeriod
								value={{ fromDateTime: from, toDateTime: to }}
								onChange={value => dispatchVariables({ value, action: 'dateTime' })}
								label={areaTranslation}
								labelPeriod={{
									from: `${areaTranslation} ${t('common.strings.from').toLowerCase()}`,
									to: `${areaTranslation} ${t('common.strings.to').toLowerCase()}`
								}}
							/>
						</FormCol>
						<FormCol>
							<Col type='row'>
								<Number noForm withLocale={false} onChange={value => dispatchVariables({ value, action: 'billNumberFrom' })} value={variables.billNumberFrom} label={t('statistic_section.strings.bill_number_from_to')} shrink inline/>
								<Number noForm withLocale={false} onChange={value => dispatchVariables({ value, action: 'billNumberTo' })} value={variables.billNumberTo} shrink inline/>
							</Col>
							<Col type='row'>
								<Number noForm precision={2} onChange={value => dispatchVariables({ value, action: 'totalFrom' })} value={variables.totalFrom} label={t('statistic_section.strings.total_from_to')} shrink inline/>
								<Number noForm precision={2} onChange={value => dispatchVariables({ value, action: 'totalTo' })} value={variables.totalTo} shrink inline/>
							</Col>
						</FormCol>
						<FormCol>
							<Select
								noForm
								label={t('common.fields.discount')}
								value={variables.discountType}
								isClearable
								options={[
									{ label: `% ${t('statistic_section.strings.button')}`, value: 96 },
									{ label: `2% ${t('statistic_section.strings.button')}`, value: 97 },
									{ label: `${discountNumberTransl} 1`, value: 11 },
									{ label: `${discountNumberTransl} 2`, value: 12 },
									{ label: `${discountNumberTransl} 3`, value: 13 },
									{ label: `${discountNumberTransl} 4`, value: 14 },
									{ label: `${discountNumberTransl} 5`, value: 15 },
									{ label: `${discountNumberTransl} %`, value: 16 },
									{ label: t('statistic_section.strings.promotion_discount'), value: 50 },
									{ label: t('statistic_section.strings.staff_discount'), value: 99 },
									{ label: t('statistic_section.strings.employee_consumption'), value: 98 }
								]}
								onChange={option => dispatchVariables({ value: option?.value || null, action: 'discountType' })}
							/>
							<Col type='row'>
								<Number noForm withLocale={false} onChange={value => dispatchVariables({ value, action: 'discountFrom' })} value={variables.discountFrom} label={t('statistic_section.strings.from_to')} shrink inline/>
								<Number noForm withLocale={false} onChange={value => dispatchVariables({ value, action: 'discountTo' })} value={variables.discountTo} shrink inline/>
							</Col>
						</FormCol>
					</FormRow>
					<ContentBlock label={t('common.buttons.filter')}>
						<CardsRow>
							<CardCol>
								<CashRegisters
									value={variables.cashRegisters}
									onChange={value => dispatchVariables({ value, action: 'cashRegisters' })}
								/>
							</CardCol>
							<CardCol>
								<FilterCardRemote
									listQuery={listProducts}
									label={t('common.strings.product')}
									allItemsLabel={t('common.strings.all_products')}
									value={variables.products}
									onChange={value => dispatchVariables({ value, action: 'products' })}
									withSearch={true}
								/>
							</CardCol>
							<CardCol>
								<FilterCardRemote
									listQuery={listUsers}
									label={t('statistic_section.strings.user')}
									allItemsLabel={t('statistic_section.strings.all_users')}
									value={variables.users}
									onChange={value => dispatchVariables({ value, action: 'users' })}
									withSearch={true}
								/>
							</CardCol>
							<CardCol>
								<FilterCardRemote
									listQuery={listUsers}
									label={t('common.strings.canceled')}
									allItemsLabel={t('statistic_section.strings.all_canceled')}
									value={variables.cancellationUsers}
									onChange={value => dispatchVariables({ value, action: 'cancellationUsers' })}
									withSearch={true}
								/>
							</CardCol>
							<CardCol>
								<FilterCardRemote
									listQuery={listCreditClients}
									label={t('statistic_section.strings.customer_credit')}
									allItemsLabel={t('statistic_section.strings.all_customer_credit')}
									value={variables.creditCustomers}
									onChange={value => dispatchVariables({ value, action: 'creditCustomers' })}
									withSearch={true}
								/>
							</CardCol>
							{/* <Col xs={2}> */}
							{/* 	<CardItemList */}
							{/* 		items={variables.actionGroups} */}
							{/* 		height={200} */}
							{/* 		label='Aktionsgruppe' */}
							{/* 		// onAddItem={() => setShowModal('timeperiod')} */}
							{/* 	/> */}
							{/* </Col> */}
						</CardsRow>
					</ContentBlock>
					<ContentBlock label={t('statistic_section.strings.payment_method')}>
						<Row>
							<Col xs={3}>
								<Select
									value={variables.paymentType}
									label={t('statistic_section.strings.payment_method')}
									isClearable
									options={[
										{ label: t('statistic_section.strings.bar'), value: 'bar' },
										{ label: t('statistic_section.strings.credit'), value: 'credit' }
									]}
									onChange={option => dispatchVariables({ value: option?.value || null, action: 'paymentType' })}
								/>
								<Select
									value={variables.taxType}
									label={t('common.strings.vat')}
									isClearable
									options={[
										{ label: t('closing_section.fields.vat_normal'), value: 'normal' },
										{ label: t('closing_section.fields.vat_reduced'), value: 'reduced' },
										{ label: t('closing_section.fields.vat_free'), value: 'free' }
									]}
									onChange={option => dispatchVariables({ value: option?.value || null, action: 'taxType' })}
								/>
							</Col>
							<Col xs={3}>
								<Select
									value={variables.inHouse}
									label={t('statistic_section.strings.in_cafe')}
									isClearable
									options={[
										{ label: t('statistic_section.strings.with_in_cafe'), value: true },
										{ label: t('statistic_section.strings.without_in_cafe'), value: false }
									]}
									onChange={option => dispatchVariables({ value: option?.value || null, action: 'inHouse' })}
								/>
								<Select
									value={variables.hasCancelledItems}
									label={t('statistic_section.strings.show_immediate_cancellation')}
									isClearable
									options={[
										{ label: t('statistic_section.strings.with_immediate_cancellation'), value: true },
										{ label: t('statistic_section.strings.without_immediate_cancellation'), value: false }
									]}
									onChange={option => dispatchVariables({ value: option?.value || null, action: 'hasCancelledItems' })}
								/>
							</Col>
							<Col xs={3}>
								<Select
									value={variables.loyaltyCardType}
									label={t('statistic_section.strings.customer_card')}
									isClearable
									options={[
										{ label: t('statistic_section.strings.in_cafe_cards'), value: 'inHouse' },
										{ label: t('statistic_section.strings.loyalty_cards'), value: 'loyalty' },
										{ label: t('statistic_section.strings.debit_card'), value: 'debit' }
									]}
									onChange={option => dispatchVariables({ value: option?.value || null, action: 'loyaltyCardType' })}
								/>
								<Text noForm onChange={e => dispatchVariables({ value: e.target.value, action: 'loyaltyCardNumber' })} value={variables.loyaltyCardNumber || ''} label={t('common.fields.code')} />
							</Col>
						</Row>
					</ContentBlock>
					<ContentBlock label={t('common.fields.amount')}>
						<Row>
							<Col xs={2}>
								<MoreLessEquals onChange={value => dispatchVariables({ value, action: 'billPositions' })} value={variables.billPositions} label={t('statistic_section.strings.bill_positions')} />
							</Col>
							<Col xs={2}>
								<MoreLessEquals onChange={value => dispatchVariables({ value, action: 'cashGiven' })}value={variables.cashGiven} label={t('statistic_section.strings.given_amount')} />
							</Col>
							<Col xs={2}>
								<MoreLessEquals onChange={value => dispatchVariables({ value, action: 'change' })} value={variables.change} label={t('statistic_section.strings.change_amount')} />
							</Col>
							<Col xs={2}>
								<MoreLessEquals onChange={value => dispatchVariables({ value, action: 'billOpen' })} value={variables.billOpen} label={t('statistic_section.strings.bill_open_seconds')} />
							</Col>
							<Col xs={2}>
								<Number noForm onChange={value => dispatchVariables({ value, action: 'cancelledAmountFrom' })} value={variables.cancelledAmountFrom} placeholder='Von' label={t('statistic_section.strings.immediate_cancellation_amount')} shrink />
								<Number noForm onChange={value => dispatchVariables({ value, action: 'cancelledAmountTo' })} value={variables.cancelledAmountTo} placeholder='Bis' shrink />
							</Col>
							<Col xs={2}>
								<MoreLessEquals onChange={value => dispatchVariables({ value, action: 'deletedPositions' })} value={variables.deletedPositions} label={t('statistic_section.strings.immediate_cancellation_number')} />
							</Col>
						</Row>
					</ContentBlock>
					<Divider/>
					<Col type='row'>
						{/* <Button text='ABC Statistik'/> */}
						<Button text={t('common.buttons.calculate')} onClick={handleSearchBills}/>
					</Col>
				</ExpandableBlocks>
			</ContentBlock>
			{called &&
				<ContentBlockLoading
					alwaysExpanded
					isLoading={loading}
					topBorder
				>
					<Row>
						<Col xs={6}>
							<DxDataTable
								id='billsTable'
								label={t('statistic_section.sub_sections.bill_search')}
								height={getTableHeight}
								columns={billCols}
								data={data}
								withSearch
								onRowClick={rowData => setSelectedBill(rowData)}
								columnAutoWidth={false}
								excelExport
							/>
						</Col>
						{selectedBill &&
							<Col xs={6}>
								<DxDataTable
									label={selectedBill.userName}
									columns={billItemCols}
									data={selectedBill.items || []}
									withSearch
									columnAutoWidth={false}
									{...status === 'ERROR' && { noDataText: t('server_errors.request_failed') }}
								/>
								{selectedBill.loyaltyCard && <>
									<Divider height={10}/>
									<ContentHeader title={`${t('statistic_section.strings.customer_card')} ${selectedBill.loyaltyCard.id} ${selectedBill.loyaltyCard.name}`}/>
								</>}
							</Col>
						}
					</Row>
				</ContentBlockLoading>
			}
			{/* <ContentBlock blockId='2' startExpanded={false} label='Berechnungsergebnis ABC Statistik '> */}
			{/* </ContentBlock> */}
		</ExpandableBlocks>

	)
}

export default BillSearch
